import { X, CalendarRange } from "lucide-react";
import { todayISO, currentMonth, shiftMonth, monthRange } from "../lib/records";

// Shared filter furniture, so every list screen behaves the same way.
//
// Two pieces:
//   <DateRangePresets>  one-click ranges, because "this month" shouldn't need
//                       two date pickers to express.
//   <ActiveFilters>     a chip per active filter with an individual clear and
//                       a clear-all. Without it a filtered-empty table looks
//                       identical to genuinely-empty data, which is the single
//                       most common way people get confused by a list screen.

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/** The ranges people actually ask for, as {from, to} pairs. */
export const DATE_PRESETS = [
  { key: "today", label: "Today", range: () => ({ from: todayISO(), to: todayISO() }) },
  { key: "7d", label: "Last 7 days", range: () => ({ from: daysAgo(6), to: todayISO() }) },
  { key: "30d", label: "Last 30 days", range: () => ({ from: daysAgo(29), to: todayISO() }) },
  { key: "month", label: "This month", range: () => monthRange(currentMonth()) },
  {
    key: "lastMonth",
    label: "Last month",
    range: () => {
      const prev = shiftMonth(currentMonth(), -1);
      const [y, m] = prev.split("-").map(Number);
      const last = new Date(y, m, 0).getDate();
      return { from: `${prev}-01`, to: `${prev}-${String(last).padStart(2, "0")}` };
    },
  },
];

/** Which preset, if any, the current from/to corresponds to. */
export function activePresetKey(from, to) {
  return DATE_PRESETS.find((p) => {
    const r = p.range();
    return r.from === from && r.to === to;
  })?.key;
}

export function DateRangePresets({ from, to, onChange, className = "" }) {
  const active = activePresetKey(from, to);
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      <CalendarRange size={14} className="shrink-0 text-gray-400" />
      {DATE_PRESETS.map((p) => {
        const on = active === p.key;
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => {
              const r = p.range();
              onChange(r.from, r.to);
            }}
            className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
              on
                ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-sm"
                : "bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
            }`}
          >
            {p.label}
          </button>
        );
      })}
      {!active && (from || to) && (
        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
          Custom
        </span>
      )}
    </div>
  );
}

/**
 * @param filters  [{ label, value, onClear }] — only entries with a value show
 * @param onClearAll  clears everything at once
 */
export function ActiveFilters({ filters = [], onClearAll, resultCount, className = "" }) {
  const active = filters.filter((f) => f && f.value);
  if (active.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="text-xs font-medium text-gray-500">
        {resultCount != null ? `${resultCount} matching · ` : ""}
        Filtered by
      </span>
      {active.map((f) => (
        <span
          key={f.label}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 py-1 pl-2.5 pr-1 text-xs font-medium text-emerald-800"
        >
          <span className="text-emerald-600/70">{f.label}:</span>
          {f.value}
          {f.onClear && (
            <button
              type="button"
              onClick={f.onClear}
              title={`Clear ${f.label.toLowerCase()}`}
              className="rounded-full p-0.5 text-emerald-600 transition-colors hover:bg-emerald-200 hover:text-emerald-900"
            >
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      {active.length > 1 && onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          className="rounded-full px-2 py-1 text-xs font-semibold text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
